import { createContext, h } from 'preact';
import { useContext, useEffect, useRef, useState } from 'preact/hooks';

import { createWorld } from './world.js';
import {
  DRAFT_FIELDS,
  describeDraft,
  getEditorSession,
  pickDraftFields,
  registerEditorSession,
  subscribeEditorSession,
} from './editorSession.js';
import {
  applyDraftToMonacoModels,
  codeViewForDraft,
  updateMonacoModelsIntoEndpoint,
} from '../utils/editorUrils.js';

const WorldContext = createContext(null);

export function WorldProvider(props) {
  const worldRef = useRef(null);
  if (!worldRef.current) {
    worldRef.current = createWorld();
  }
  return h(WorldContext.Provider, { value: worldRef.current }, props.children);
}

export function useOptionalWorld() {
  return useContext(WorldContext);
}

export function useWorldObjects() {
  const world = useOptionalWorld();
  const [, setVersion] = useState(world ? world.getVersion() : 0);

  useEffect(
    function subscribeToWorld() {
      if (!world) {
        return undefined;
      }
      return world.subscribe(() => setVersion(world.getVersion()));
    },
    [world]
  );

  return world ? Array.from(world.registry.values()) : [];
}

export function useObjectRegistration(object) {
  const world = useOptionalWorld();
  const objectRef = useRef(object);
  objectRef.current = object;

  useEffect(
    function unregisterOnUnmount() {
      if (!world) {
        return undefined;
      }
      return () => world.unregister(object.id);
    },
    [world, object.id]
  );

  useEffect(function registerCurrent() {
    if (world) {
      world.register(objectRef.current);
    }
  });
}

// ---------------------------------------------------------------------------
// Editor session (editorSession.js): what the endpoint editor holds right now.

/** The current editor session, re-rendering when an editor mounts or unmounts. */
export function useEditorSession() {
  const [session, setSession] = useState(getEditorSession());

  useEffect(function subscribe() {
    setSession(getEditorSession());
    return subscribeEditorSession(setSession);
  }, []);

  return session;
}

/**
 * Registers the mounted editor as the copilot's editor session. `options`
 * is read through a ref on every call, so the registered accessors always
 * see the latest model, form handlers and Monaco models without
 * re-registering on each keystroke.
 *
 * options: { mode: 'edit' | 'new', version, endpointId, model, serverModel,
 *            onEndpointChange(name, value), getEditorModels(),
 *            setCodeView?(area, index) — the sidebar's tab switch, so a draft
 *            brings the script (or the changed schema) in front,
 *            save?: () => Promise<{ id, name }> — the route's own persist call }
 */
export function useCopilotEditorSession(options) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const idRef = useRef(null);
  if (!idRef.current) {
    idRef.current = `editor-session-${Math.random().toString(36).slice(2)}`;
  }

  useEffect(
    function register() {
      const id = idRef.current;

      function getDefinition() {
        const { model, getEditorModels } = optionsRef.current;
        if (!model) {
          return null;
        }
        return updateMonacoModelsIntoEndpoint(getEditorModels(), model);
      }

      function isDirty() {
        const { mode, serverModel } = optionsRef.current;
        if (mode === 'new') {
          return true;
        }
        const live = getDefinition();
        if (!live || !serverModel) {
          return false;
        }
        return DRAFT_FIELDS.some(
          field => JSON.stringify(live[field]) !== JSON.stringify(serverModel[field])
        );
      }

      function applyDraft(patch) {
        const { model, onEndpointChange, getEditorModels, setCodeView } = optionsRef.current;
        if (!model) {
          throw new Error('No endpoint is open in the editor');
        }
        const draft = pickDraftFields(patch || {});
        if (!Object.keys(draft).length) {
          throw new Error('The draft carries no editable field');
        }
        const before = getDefinition();
        const changes = describeDraft(before, draft);

        Object.keys(draft).forEach(field => {
          if (field === 'schemas') {
            onEndpointChange('schemas', { ...(model.schemas || {}), ...draft.schemas });
            return;
          }
          onEndpointChange(field, draft[field]);
        });
        const written = applyDraftToMonacoModels(getEditorModels(), draft);
        const shownView = codeViewForDraft(before, draft);
        if (shownView && typeof setCodeView === 'function') {
          setCodeView(shownView, 0);
        }

        return {
          applied: Object.keys(draft),
          changes,
          editorModelsWritten: written.length,
          ...(shownView ? { shownView } : {}),
        };
      }

      return registerEditorSession({
        id,
        get mode() {
          return optionsRef.current.mode;
        },
        get version() {
          return optionsRef.current.version;
        },
        get endpointId() {
          return optionsRef.current.endpointId || null;
        },
        getDefinition,
        isDirty,
        applyDraft,
        save() {
          const { save } = optionsRef.current;
          if (typeof save !== 'function') {
            return Promise.reject(new Error('Saving is not available on this page'));
          }
          return save();
        },
      });
    },
    []
  );
}
